import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/shared/Button';
import TeamAppOverview from '../components/shared/TeamAppOverview';
import ScoreSheetPages, { RowChecks, TeamChecks } from '../components/print/ScoreSheetPages';
import { HitsterEvent, EventRound, EventTeam, TeamRoundData, TeamSongScore } from '../types';
import { getTimelineLayout, sortRoundSongs } from '../utils/roundTimeline';
import './PrintScoreSheet.css';
import './RoundScoring.css';

const API_BASE = process.env.REACT_APP_API_URL || '';

const normalizeSongScoresForRound = (songs: EventRound['songs'], existingScores: TeamSongScore[] = []): TeamSongScore[] => {
  const existingBySongId = new Map(existingScores.map(score => [score.songId, score]));

  return sortRoundSongs(songs).map((song, index) => {
    const existing = existingBySongId.get(song.id);
    const titleCorrect = existing?.titleCorrect || false;
    const artistCorrect = existing?.artistCorrect || false;
    const yearCorrect = existing?.yearCorrect || false;
    const positionCorrect = existing?.positionCorrect || false;

    return {
      songId: song.id,
      songOrder: index + 1, // Always use sequential position (1,2,3...) regardless of stored order field
      titleCorrect,
      artistCorrect,
      yearCorrect,
      positionCorrect,
      points:
        Number(titleCorrect)
        + Number(artistCorrect)
        + Number(yearCorrect)
        + Number(positionCorrect),
    };
  });
};

const RoundScoring: React.FC = () => {
  const { eventId, roundId } = useParams<{ eventId: string; roundId: string }>();
  const navigate = useNavigate();
  
  const [event, setEvent] = useState<HitsterEvent | null>(null);
  const [round, setRound] = useState<EventRound | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [selectedTeam, setSelectedTeam] = useState<EventTeam | null>(null);
  const [viewMode, setViewMode] = useState<'scores' | 'timeline' | 'teamApp' | 'scan'>('scores');

  // Scan state
  interface CardScanResult { cardNumber: number | null; title: string | null; artist: string | null; year: string | null; positie: boolean | null; }
  const [scanResults, setScanResults] = useState<Record<string, CardScanResult | null>>({});
  const [scanLoading, setScanLoading] = useState<Record<string, boolean>>({});
  const [scanErrors, setScanErrors] = useState<Record<string, string>>({});
  const [scanApplied, setScanApplied] = useState<Record<string, boolean>>({});
  const fileInputRef = useRef<HTMLInputElement>(null);
  const pendingScanSongIdRef = useRef<string | null>(null);
  const [jsonInput, setJsonInput] = useState('');
  const [jsonParseError, setJsonParseError] = useState('');
  const [scanOverrides, setScanOverrides] = useState<Record<string, {title?: boolean; artist?: boolean; year?: boolean}>>({});

  // Review modal state (shown after individual card scan)
  interface ReviewModal { songId: string; song: { id: string; title: string; artist: string; year: number }; result: CardScanResult; }
  const [reviewModal, setReviewModal] = useState<ReviewModal | null>(null);
  const [reviewOverrides, setReviewOverrides] = useState<{title?: boolean; artist?: boolean; year?: boolean}>({});

  // Scan submissions from participants (stored per team)
  interface ScanSubmission { songId: string; cardNumber: number | null; title: string | null; artist: string | null; year: string | null; submittedAt: string; reviewStatus: 'pending' | 'approved' | 'rejected'; }
  interface ScanModalEntry { team: EventTeam; submission: ScanSubmission; }
  interface ScanSubmissionsModal { songId: string; songTitle: string; songArtist: string; songYear: number; entries: ScanModalEntry[]; }
  const [teamScanSubmissions, setTeamScanSubmissions] = useState<Record<string, ScanSubmission[]>>({});
  const [scanSubmissionsModal, setScanSubmissionsModal] = useState<ScanSubmissionsModal | null>(null);

  // Start year mode: shared (same for all) or perTeam (different per team)
  const [startYearMode, setStartYearMode] = useState<'shared' | 'perTeam'>('shared');
  const [sharedStartYear, setSharedStartYear] = useState(1990);
  
  // Team round data (startYear and scores)
  const [teamStartYears, setTeamStartYears] = useState<Record<string, number>>({});
  const [teamScores, setTeamScores] = useState<Record<string, TeamSongScore[]>>({});
  
  // Auto-save refs
  const hasInitialized = useRef(false);
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const pendingChanges = useRef(false);

  // Generate random unique years for teams (range 1950-2020)
  const generateRandomYears = (teams: EventTeam[]) => {
    const minYear = 1950;
    const maxYear = 2020;
    const usedYears = new Set<number>();
    const newYears: Record<string, number> = {};
    
    teams.forEach(team => {
      let year: number;
      do {
        year = Math.floor(Math.random() * (maxYear - minYear + 1)) + minYear;
      } while (usedYears.has(year) && usedYears.size < (maxYear - minYear + 1));
      usedYears.add(year);
      newYears[team.id] = year;
    });
    
    return newYears;
  };

  // Wrapper functions that track changes
  const handleModeChange = (mode: 'shared' | 'perTeam') => {
    pendingChanges.current = true;
    setStartYearMode(mode);
    
    // Auto-generate random years when switching to perTeam mode
    if (mode === 'perTeam' && event?.teams) {
      const randomYears = generateRandomYears(event.teams);
      setTeamStartYears(randomYears);
    }
  };

  const handleSharedYearChange = (year: number) => {
    pendingChanges.current = true;
    setSharedStartYear(year);
  };

  // Save round's start year immediately (for newly generated years)
  const saveRoundStartYear = async (year: number) => {
    if (!event || !roundId) return;
    
    const updatedRounds = event.rounds.map(r => {
      if (r.id === roundId) {
        return { ...r, startYear: year, startYearMode: 'shared' as const };
      }
      return r;
    });
    
    const updatedEvent = { ...event, rounds: updatedRounds };
    
    try {
      await fetch(`/api/events/${eventId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updatedEvent),
      });
      // Update local state so we don't regenerate if deps change
      setEvent(updatedEvent);
      setRound(updatedRounds.find(r => r.id === roundId) || null);
    } catch (err) {
      console.error('Failed to save round start year:', err);
    }
  };
  
  // Load event data
  useEffect(() => {
    loadEvent();
  }, [eventId]);

  useEffect(() => {
    if (event && roundId) {
      const r = event.rounds.find(r => r.id === roundId);
      setRound(r || null);
      
      // Load start year mode from round
      if (r) {
        setStartYearMode(r.startYearMode || 'shared');
        
        // If round already has a saved start year, use it
        // Otherwise generate a random year different from other rounds
        if (r.startYear) {
          setSharedStartYear(r.startYear);
        } else {
          // Get years used by other rounds
          const usedYears = new Set(
            event.rounds
              .filter(round => round.id !== roundId && round.startYear)
              .map(round => round.startYear!)
          );
          
          // Generate a random year between 1950-2020 that's not used
          const minYear = 1950;
          const maxYear = 2020;
          let newYear: number;
          let attempts = 0;
          do {
            newYear = Math.floor(Math.random() * (maxYear - minYear + 1)) + minYear;
            attempts++;
          } while (usedYears.has(newYear) && attempts < 100);
          
          setSharedStartYear(newYear);
          // Immediately save the new year to the server
          saveRoundStartYear(newYear);
        }
      }
      
      // Initialize team data from existing roundData
      const startYears: Record<string, number> = {};
      const scores: Record<string, TeamSongScore[]> = {};
      const scanSubs: Record<string, any[]> = {};
      const orderedRoundSongs = r ? sortRoundSongs(r.songs) : [];
      
      (event.teams || []).forEach(team => {
        const roundData = team.roundData?.find(rd => rd.roundId === roundId);
        if (roundData) {
          startYears[team.id] = roundData.startYear;
          scores[team.id] = normalizeSongScoresForRound(orderedRoundSongs, roundData.songScores);
          scanSubs[team.id] = (roundData as any).scanSubmissions || [];
        } else {
          startYears[team.id] = r?.startYear || 1990; // Use round's start year as default
          scores[team.id] = normalizeSongScoresForRound(orderedRoundSongs);
          scanSubs[team.id] = [];
        }
      });
      
      setTeamStartYears(startYears);
      setTeamScores(scores);
      setTeamScanSubmissions(scanSubs);
      
      // Select first team
      if (event.teams && event.teams.length > 0 && !selectedTeam) {
        setSelectedTeam(event.teams[0]);
      }
    }
  }, [event, roundId]);

  // Reset scan state when switching teams
  useEffect(() => {
    setScanResults({});
    setScanLoading({});
    setScanErrors({});
    setScanApplied({});
    setScanOverrides({});
    setJsonInput('');
    setJsonParseError('');
  }, [selectedTeam]);

  const handleJsonBatch = () => {
    if (!round) return;
    try {
      const parsed = JSON.parse(jsonInput);
      if (!Array.isArray(parsed)) throw new Error('Verwacht een JSON array');
      const songs = sortRoundSongs(round.songs);
      const newResults: Record<string, CardScanResult> = {};
      let matched = 0;
      for (const entry of parsed) {
        const cardNum = entry.nummer ?? entry.cardNumber ?? entry.number;
        if (cardNum == null) continue;
        const songIdx = Number(cardNum) - 1;
        if (songIdx < 0 || songIdx >= songs.length) continue;
        const song = songs[songIdx];
        const rawPositie = entry.positie ?? entry.position ?? entry.positionCorrect ?? null;
        newResults[song.id] = {
          cardNumber: Number(cardNum),
          title: entry.titel ?? entry.title ?? null,
          artist: entry.artiest ?? entry.artist ?? null,
          year: entry.jaar != null ? String(entry.jaar) : (entry.year != null ? String(entry.year) : null),
          positie: rawPositie != null ? Boolean(rawPositie) : null,
        };
        matched++;
      }
      if (matched === 0) {
        setJsonParseError('Geen kaartjes gevonden in de JSON');
        return;
      }
      // Auto-bereken positie op basis van volgorde in JSON-array (= volgorde op antwoordformulier = tijdlijn)
      // Positie is correct als het jaar >= voorganger en <= opvolger in de array is
      const orderedEntries = parsed
        .map((entry: any) => {
          const cardNum = entry.nummer ?? entry.cardNumber ?? entry.number;
          if (cardNum == null) return null;
          const songIdx = Number(cardNum) - 1;
          if (songIdx < 0 || songIdx >= songs.length) return null;
          const rawYear = entry.jaar != null ? Number(entry.jaar) : (entry.year != null ? Number(entry.year) : null);
          return { songId: songs[songIdx].id, year: rawYear };
        })
        .filter((e: any) => e != null) as { songId: string; year: number | null }[];
      orderedEntries.forEach((entry, i) => {
        if (!newResults[entry.songId] || newResults[entry.songId].positie != null) return;
        if (entry.year == null) return;
        const prevYear = i > 0 && orderedEntries[i - 1].year != null ? orderedEntries[i - 1].year! : -Infinity;
        const nextYear = i < orderedEntries.length - 1 && orderedEntries[i + 1].year != null ? orderedEntries[i + 1].year! : Infinity;
        newResults[entry.songId].positie = entry.year >= prevYear && entry.year <= nextYear;
      });
      setScanResults(prev => ({ ...prev, ...newResults }));
      // Auto-apply songs where all fields match
      const autoApplied: Record<string, boolean> = {};
      if (selectedTeam) {
        for (const [songId, result] of Object.entries(newResults)) {
          if (!result) continue;
          const song = songs.find(s => s.id === songId);
          if (!song) continue;
          const tm = result.title != null ? isMatch(result.title, song.title) : null;
          const am = result.artist != null ? isMatch(result.artist, song.artist) : null;
          const ym = result.year != null ? isMatch(result.year, String(song.year)) : null;
          const pm = result.positie;
          if (tm === true && am === true && ym === true) {
            setScore(selectedTeam.id, songId, 'titleCorrect', true);
            setScore(selectedTeam.id, songId, 'artistCorrect', true);
            setScore(selectedTeam.id, songId, 'yearCorrect', true);
            // Als jaar klopt, is positie ook correct (of gebruik expliciete waarde als aanwezig)
            setScore(selectedTeam.id, songId, 'positionCorrect', pm != null ? pm : true);
            autoApplied[songId] = true;
          } else if (pm != null) {
            setScore(selectedTeam.id, songId, 'positionCorrect', pm);
          }
        }
      }
      setScanApplied(prev => {
        const reset: Record<string, boolean> = {};
        Object.keys(newResults).forEach(k => { reset[k] = autoApplied[k] || false; });
        return { ...prev, ...reset };
      });
      setJsonParseError('');
    } catch (e: any) {
      setJsonParseError(e.message || 'Ongeldige JSON');
    }
  };

  const applyAllBatchResults = () => {
    if (!round || !selectedTeam) return;
    sortRoundSongs(round.songs).forEach(song => {
      if (scanResults[song.id] && !scanApplied[song.id]) {
        applyScanResult(song.id, song);
      }
    });
  };

  const setFieldOverride = (songId: string, field: 'title' | 'artist' | 'year', value: boolean) => {
    setScanOverrides(prev => ({
      ...prev,
      [songId]: { ...(prev[songId] || {}), [field]: value },
    }));
  };

  const loadEvent = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/events/${eventId}`);
      if (response.ok) {
        const data = await response.json();
        setEvent(data);
      }
    } catch (err) {
      console.error('Error loading event:', err);
    } finally {
      setLoading(false);
    }
  };

  const updateStartYear = (teamId: string, year: number) => {
    pendingChanges.current = true;
    setTeamStartYears(prev => ({ ...prev, [teamId]: year }));
  };

  const normalizeStr = (s: string | null): string =>
    (s || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '') // strip combining diacritics (é→e, ç→c, etc.)
      .replace(/[^a-z0-9]/g, '');

  const levenshtein = (a: string, b: string): number => {
    const dp: number[] = Array.from({length: b.length + 1}, (_, j) => j);
    for (let i = 1; i <= a.length; i++) {
      let prev = dp[0];
      dp[0] = i;
      for (let j = 1; j <= b.length; j++) {
        const tmp = dp[j];
        dp[j] = a[i-1] === b[j-1] ? prev : 1 + Math.min(prev, dp[j], dp[j-1]);
        prev = tmp;
      }
    }
    return dp[b.length];
  };

  const isMatch = (scanned: string | null, correct: string): boolean => {
    const a = normalizeStr(scanned);
    if (!a) return false;
    // Split correct by commas to support multi-artist (check each part individually)
    const correctParts = correct.split(',').map(p => normalizeStr(p)).filter(p => p.length > 0);
    for (const b of correctParts) {
      if (!b) continue;
      if (a.includes(b) || b.includes(a)) return true;
      // Fuzzy match: allow up to 20% edit distance (handles typos, m'n vs mijn, etc.)
      if (a.length >= 5 && b.length >= 5) {
        if (levenshtein(a, b) / Math.max(a.length, b.length) <= 0.2) return true;
      }
    }
    return false;
  };

  const setScore = (teamId: string, songId: string, field: keyof TeamSongScore, value: boolean) => {
    pendingChanges.current = true;
    setTeamScores(prev => {
      const teamData = [...(prev[teamId] || [])];
      const songIdx = teamData.findIndex(s => s.songId === songId);
      if (songIdx >= 0) {
        teamData[songIdx] = { ...teamData[songIdx], [field]: value };
        const score = teamData[songIdx];
        teamData[songIdx].points =
          (score.titleCorrect ? 1 : 0) +
          (score.artistCorrect ? 1 : 0) +
          (score.yearCorrect ? 1 : 0) +
          (score.positionCorrect ? 1 : 0);
      }
      return { ...prev, [teamId]: teamData };
    });
  };

  const startScan = (songId: string) => {
    pendingScanSongIdRef.current = songId;
    fileInputRef.current?.click();
  };

  const handleScanFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    const songId = pendingScanSongIdRef.current;
    if (!file || !songId || !selectedTeam) return;
    e.target.value = '';
    setScanLoading(prev => ({ ...prev, [songId]: true }));
    setScanErrors(prev => ({ ...prev, [songId]: '' }));
    setScanResults(prev => ({ ...prev, [songId]: null }));
    setScanApplied(prev => ({ ...prev, [songId]: false }));
    try {
      const formData = new FormData();
      formData.append('image', file);
      const response = await fetch(`${API_BASE}/api/scan-card`, { method: 'POST', body: formData });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error || 'Scan mislukt');
      }
      const result = await response.json();
      setScanResults(prev => ({ ...prev, [songId]: result }));
      // Open review modal so admin can verify/adjust before applying
      if (round) {
        const song = round.songs.find(s => s.id === songId);
        if (song) {
          setReviewModal({ songId, song, result });
          setReviewOverrides({});
        }
      }
    } catch (err: any) {
      setScanErrors(prev => ({ ...prev, [songId]: err.message || 'Scan mislukt' }));
    } finally {
      setScanLoading(prev => ({ ...prev, [songId]: false }));
    }
  };

  const applyScanResult = (songId: string, song: { title: string; artist: string; year: number }) => {
    if (!selectedTeam || !scanResults[songId]) return;
    const result = scanResults[songId]!;
    const ov = scanOverrides[songId] || {};
    if (result.title !== null) setScore(selectedTeam.id, songId, 'titleCorrect', ov.title !== undefined ? ov.title : isMatch(result.title, song.title));
    if (result.artist !== null) setScore(selectedTeam.id, songId, 'artistCorrect', ov.artist !== undefined ? ov.artist : isMatch(result.artist, song.artist));
    if (result.year !== null) setScore(selectedTeam.id, songId, 'yearCorrect', ov.year !== undefined ? ov.year : isMatch(result.year, String(song.year)));
    if (result.positie != null) setScore(selectedTeam.id, songId, 'positionCorrect', result.positie);
    setScanApplied(prev => ({ ...prev, [songId]: true }));
  };

  const toggleScore = (teamId: string, songId: string, field: keyof TeamSongScore) => {
    if (field === 'songId' || field === 'songOrder' || field === 'points') return;
    
    pendingChanges.current = true;
    setTeamScores(prev => {
      const teamData = [...(prev[teamId] || [])];
      const songIdx = teamData.findIndex(s => s.songId === songId);
      if (songIdx >= 0) {
        const newValue = !teamData[songIdx][field];
        teamData[songIdx] = {
          ...teamData[songIdx],
          [field]: newValue,
        };
        // Recalculate points
        const score = teamData[songIdx];
        teamData[songIdx].points = 
          (score.titleCorrect ? 1 : 0) +
          (score.artistCorrect ? 1 : 0) +
          (score.yearCorrect ? 1 : 0) +
          (score.positionCorrect ? 1 : 0);
      }
      return { ...prev, [teamId]: teamData };
    });
  };

  const getTeamTotalScore = (teamId: string) => {
    const scores = teamScores[teamId] || [];
    return scores.reduce((sum, s) => sum + s.points, 0);
  };

  const exportRoundJson = () => {
    if (!event || !round) return;
    const orderedSongs = sortRoundSongs(round.songs);
    const data = {
      event: event.name,
      round: round.name,
      exportedAt: new Date().toISOString(),
      songs: orderedSongs.map(s => ({
        id: s.id,
        cardNumber: s.order,
        title: s.title,
        artist: s.artist,
        year: s.year,
      })),
      teams: (event.teams || []).map(team => {
        const startYear = startYearMode === 'shared' ? sharedStartYear : (teamStartYears[team.id] || sharedStartYear);
        const scores = teamScores[team.id] || [];
        const timeline = getTimelineLayout(round.songs, startYear);
        return {
          name: team.name,
          startYear,
          totalScore: getTeamTotalScore(team.id),
          scores: orderedSongs.map(song => {
            const sc = scores.find(s => s.songId === song.id);
            const tCard = timeline.orderedCards.find(c => c.song.id === song.id);
            return {
              songId: song.id,
              cardNumber: song.order,
              title: song.title,
              artist: song.artist,
              year: song.year,
              positie: tCard ? tCard.side === 'left' ? 'links' : 'rechts' : null,
              timelinePosition: tCard?.timelinePosition ?? null,
              titleCorrect: sc?.titleCorrect ?? false,
              artistCorrect: sc?.artistCorrect ?? false,
              yearCorrect: sc?.yearCorrect ?? false,
              positionCorrect: sc?.positionCorrect ?? false,
              points: sc?.points ?? 0,
            };
          }),
        };
      }),
    };
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${event.name}-${round.name}.json`.replace(/\s+/g, '-').toLowerCase();
    a.click();
    URL.revokeObjectURL(url);
  };

  const getTeamOverallScore = (team: EventTeam) => {
    // Sum all rounds
    return (team.roundData || []).reduce((sum, rd) => sum + rd.totalScore, 0);
  };

  // Get total score helper (needs to be stable for useCallback)
  const calculateTeamTotal = useCallback((teamId: string) => {
    const scores = teamScores[teamId] || [];
    return scores.reduce((sum, s) => sum + s.points, 0);
  }, [teamScores]);

  const saveScores = useCallback(async () => {
    if (!event || !hasInitialized.current) return;
    setSaving(true);

    // Build per-team scores payload
    const teamScoresPayload: Record<string, typeof teamScores[string]> = {};
    (event.teams || []).forEach(team => {
      teamScoresPayload[team.id] = teamScores[team.id] || [];
    });

    try {
      // Use the targeted round-scores endpoint so we never overwrite other rounds
      const response = await fetch(`${API_BASE}/api/events/${event.id}/rounds/${roundId}/scores`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          teamScores: teamScoresPayload,
          startYear: sharedStartYear,
          startYearMode,
          teamStartYears,
        }),
      });
      if (response.ok) {
        const updatedEvent = await response.json();
        setEvent(updatedEvent);
        const updatedRound = updatedEvent.rounds?.find((r: typeof round) => r?.id === roundId) || null;
        setRound(updatedRound);
        setLastSaved(new Date());
      } else {
        console.error('Error saving scores:', response.status);
      }
    } catch (err) {
      console.error('Error saving scores:', err);
    }

    setSaving(false);
  }, [event, roundId, teamStartYears, teamScores, startYearMode, sharedStartYear]);

  // Auto-save when scores or start years change
  useEffect(() => {
    if (!hasInitialized.current) return;
    if (!pendingChanges.current) return;
    
    // Clear existing timeout
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    
    // Debounce save by 1 second
    saveTimeoutRef.current = setTimeout(() => {
      pendingChanges.current = false;
      saveScores();
    }, 1000);
    
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [teamScores, teamStartYears, startYearMode, sharedStartYear, saveScores]);

  // Mark as initialized after first data load
  useEffect(() => {
    if (event && round && Object.keys(teamScores).length > 0) {
      // Small delay to prevent immediate save after load
      setTimeout(() => {
        hasInitialized.current = true;
      }, 500);
    }
  }, [event, round, teamScores]);

  const applyReviewModal = () => {
    if (!reviewModal || !selectedTeam) return;
    const { songId, song, result } = reviewModal;
    const ov = reviewOverrides;
    if (result.title !== null)  setScore(selectedTeam.id, songId, 'titleCorrect',  ov.title  !== undefined ? ov.title  : isMatch(result.title,  song.title));
    if (result.artist !== null) setScore(selectedTeam.id, songId, 'artistCorrect', ov.artist !== undefined ? ov.artist : isMatch(result.artist, song.artist));
    if (result.year !== null)   setScore(selectedTeam.id, songId, 'yearCorrect',   ov.year   !== undefined ? ov.year   : isMatch(result.year,   String(song.year)));
    if (result.positie != null) setScore(selectedTeam.id, songId, 'positionCorrect', result.positie);
    setScanApplied(prev => ({ ...prev, [songId]: true }));
    setReviewModal(null);
  };

  // Open scan-submissions modal for a song (shows all team submissions for that song)
  const openScanSubmissionsModal = useCallback((songId: string, _songTitle: string) => {
    if (!round || !event) return;
    const song = round.songs.find(s => s.id === songId);
    if (!song) return;
    const entries: { team: EventTeam; submission: any }[] = [];
    (event.teams || []).forEach(team => {
      const subs = teamScanSubmissions[team.id] || [];
      const sub = subs.find((s: any) => s.songId === songId);
      if (sub) entries.push({ team, submission: sub });
    });
    setScanSubmissionsModal({
      songId,
      songTitle: song.title,
      songArtist: song.artist,
      songYear: song.year,
      entries,
    });
  }, [round, event, teamScanSubmissions]);

  // Approve or reject a scan submission
  const reviewScanSubmission = async (teamId: string, songId: string, status: 'approved' | 'rejected') => {
    // Approved: scores are already set automatically by the participant submission — no change needed.
    // Rejected: reset all scores for this song to 0 (manual correction).
    if (status === 'rejected') {
      setScore(teamId, songId, 'titleCorrect',    false);
      setScore(teamId, songId, 'artistCorrect',   false);
      setScore(teamId, songId, 'yearCorrect',     false);
      setScore(teamId, songId, 'positionCorrect', false);
      pendingChanges.current = true;
    }
    // Update reviewStatus
    setTeamScanSubmissions(prev => ({
      ...prev,
      [teamId]: (prev[teamId] || []).map((s: any) =>
        s.songId === songId ? { ...s, reviewStatus: status } : s
      ),
    }));
    setScanSubmissionsModal(prev => prev ? {
      ...prev,
      entries: prev.entries.map(e =>
        e.team.id === teamId && e.submission.songId === songId
          ? { ...e, submission: { ...e.submission, reviewStatus: status } }
          : e
      ),
    } : null);
    try {
      await fetch(`${API_BASE}/api/events/${eventId}/rounds/${roundId}/scan-review`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ teamId, songId, reviewStatus: status }),
      });
    } catch (err) {
      console.error('Failed to save scan review:', err);
    }
  };

  if (loading) {
    return <div className="round-scoring loading">Laden...</div>;
  }

  if (!event || !round) {
    return (
      <div className="round-scoring error">
        <p>Evenement of ronde niet gevonden</p>
        <Button onClick={() => navigate('/events')}>Terug naar Events</Button>
      </div>
    );
  }

  const teams = event.teams || [];

  // Round navigation
  const currentRoundIndex = event.rounds.findIndex(r => r.id === roundId);
  const totalRounds = event.rounds.length;
  const prevRound = currentRoundIndex > 0 ? event.rounds[currentRoundIndex - 1] : null;
  const nextRound = currentRoundIndex < totalRounds - 1 ? event.rounds[currentRoundIndex + 1] : null;

  // Count scan submissions per song (across all teams)
  const scanCounts: Record<string, number> = {};
  Object.values(teamScanSubmissions).forEach(subs => {
    (subs || []).forEach((sub: any) => {
      scanCounts[sub.songId] = (scanCounts[sub.songId] || 0) + 1;
    });
  });

  const selectedTeamChecks: TeamChecks = selectedTeam
    ? (teamScores[selectedTeam.id] || []).reduce((checks, score) => {
        checks[`${round.id}:${score.songId}`] = {
          positionCorrect: score.positionCorrect,
          yearCorrect: score.yearCorrect,
          titleCorrect: score.titleCorrect,
          artistCorrect: score.artistCorrect,
        };

        return checks;
      }, {} as TeamChecks)
    : {};

  const handleSheetToggle = (teamId: string, rowKey: string, field: keyof RowChecks) => {
    if (rowKey.includes('start-row') || rowKey.includes('empty-row')) {
      return;
    }

    const songId = rowKey.substring(rowKey.indexOf(':') + 1);
    if (!songId) {
      return;
    }

    toggleScore(teamId, songId, field as keyof TeamSongScore);
  };

  return (
    <div className="round-scoring">
      <header className="scoring-header">
        <div className="header-left">
          <Button variant="secondary" onClick={() => navigate('/events')}>
            ← Terug
          </Button>
          <div className="header-info">
            <h1>{event.name}</h1>
            <div className="round-navigation">
              <Button 
                variant="secondary" 
                disabled={!prevRound}
                onClick={() => prevRound && navigate(`/round-scoring/${eventId}/${prevRound.id}`)}
              >
                ◀ Vorige
              </Button>
              <span className="round-indicator">
                📝 {round.name} ({currentRoundIndex + 1} / {totalRounds})
              </span>
              <Button 
                variant="secondary" 
                disabled={!nextRound}
                onClick={() => nextRound && navigate(`/round-scoring/${eventId}/${nextRound.id}`)}
              >
                Volgende ▶
              </Button>
            </div>
          </div>
        </div>
        <div className="header-actions">
          <Button 
            variant="secondary" 
            onClick={() => navigate(`/print-cards/${eventId}/${roundId}`)}
          >
            🖨️ Print kaartjes
          </Button>
          <Button
            variant="secondary"
            onClick={() => navigate(`/print-answers/${eventId}/${roundId}`)}
          >
            📋 Print antwoordenformulier
          </Button>
          <Button
            variant="secondary"
            onClick={() => navigate(`/print-score-sheet/${eventId}/${roundId}`)}
          >
            📝 Print scoreformulieren
          </Button>
          <Button
            variant="secondary"
            onClick={() => navigate(`/print-score-booklets/${eventId}/${roundId}`)}
          >
            📚 Print teamboekjes
          </Button>
          <Button
            variant="secondary"
            onClick={exportRoundJson}
          >
            ⬇️ Export JSON
          </Button>
          <span className="save-status">
            {saving ? '⏳ Opslaan...' : lastSaved ? `✓ Opgeslagen ${lastSaved.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' })}` : ''}
          </span>
        </div>
      </header>

      <div className="scoring-layout">
        {/* Team selector */}
        <div className="teams-sidebar">
          <h3>Teams</h3>
          <div className="team-list">
            {teams.map(team => (
              <div
                key={team.id}
                className={`team-item ${selectedTeam?.id === team.id ? 'selected' : ''}`}
                onClick={() => setSelectedTeam(team)}
              >
                <span className="team-name">{team.name}</span>
                <span className="team-score">
                  {getTeamTotalScore(team.id)} / {round.songs.length * 4}
                </span>
              </div>
            ))}
          </div>
          
          {/* Scoreboard summary */}
          <div className="scoreboard-summary">
            <h3>🏆 Stand</h3>
            <div className="summary-list">
              {[...teams]
                .sort((a, b) => getTeamOverallScore(b) - getTeamOverallScore(a))
                .map((team, idx) => (
                  <div key={team.id} className="summary-item">
                    <span className="rank">{idx + 1}.</span>
                    <span className="team">{team.name}</span>
                    <span className="total">{getTeamOverallScore(team)}</span>
                  </div>
                ))}
            </div>
          </div>
        </div>

        {/* Score entry for selected team */}
        {selectedTeam && (
          <div className="score-entry">
            {/* Start year settings */}
            <div className="start-year-section">
              <div className="start-year-mode">
                <label>Startjaar modus:</label>
                <div className="mode-toggle">
                  <button
                    className={`mode-btn ${startYearMode === 'shared' ? 'active' : ''}`}
                    onClick={() => handleModeChange('shared')}
                  >
                    🎯 Zelfde voor alle teams
                  </button>
                  <button
                    className={`mode-btn ${startYearMode === 'perTeam' ? 'active' : ''}`}
                    onClick={() => handleModeChange('perTeam')}
                  >
                    👥 Per team verschillend
                  </button>
                </div>
              </div>
              
              {startYearMode === 'shared' ? (
                <div className="shared-year-input">
                  <label>Startjaar voor alle teams:</label>
                  <input
                    type="number"
                    min="1900"
                    max="2030"
                    value={sharedStartYear}
                    onChange={e => handleSharedYearChange(parseInt(e.target.value) || 1990)}
                  />
                </div>
              ) : (
                <div className="team-year-input">
                  <label>Startjaar voor {selectedTeam.name}:</label>
                  <input
                    type="number"
                    min="1900"
                    max="2030"
                    value={teamStartYears[selectedTeam.id] || sharedStartYear}
                    onChange={e => updateStartYear(selectedTeam.id, parseInt(e.target.value) || 1990)}
                  />
                </div>
              )}
            </div>

            <div className="team-header">
              <h3>👥 {selectedTeam.name}</h3>
              <div className="view-toggle">
                <button
                  className={`view-btn ${viewMode === 'scores' ? 'active' : ''}`}
                  onClick={() => setViewMode('scores')}
                >
                  📋 Score invoer
                </button>
                <button
                  className={`view-btn ${viewMode === 'timeline' ? 'active' : ''}`}
                  onClick={() => setViewMode('timeline')}
                >
                  🎴 Tijdlijn
                </button>
                {event?.enableTeamApp && (
                  <button
                    className={`view-btn ${viewMode === 'teamApp' ? 'active' : ''}`}
                    onClick={() => setViewMode('teamApp')}
                  >
                    📱 Team App
                  </button>
                )}
                <button
                  className={`view-btn ${viewMode === 'scan' ? 'active' : ''}`}
                  onClick={() => setViewMode('scan')}
                >
                  📷 Scan kaartjes
                </button>
              </div>
              <span className="team-start-year">
                Startjaar: {startYearMode === 'shared' ? sharedStartYear : (teamStartYears[selectedTeam.id] || sharedStartYear)}
              </span>
            </div>

            {viewMode === 'scores' && (
              <div className="digital-score-sheet-view">
                <div className="digital-score-sheet-intro">
                  <span>Digitaal nakijkblad</span>
                  <strong>Elke vink wordt direct opgeslagen en telt meteen mee op het scorebord.</strong>
                </div>
                <div className="digital-score-sheet-shell">
                  <ScoreSheetPages
                    event={event}
                    rounds={[round]}
                    teams={[selectedTeam]}
                    teamChecks={{ [selectedTeam.id]: selectedTeamChecks }}
                    onToggleCheck={handleSheetToggle}
                    scanCounts={scanCounts}
                    onScanIconClick={openScanSubmissionsModal}
                  />
                </div>
                <div className="team-total compact">
                  <span>Totaal deze ronde:</span>
                  <span className="total-score">{getTeamTotalScore(selectedTeam.id)} punten</span>
                </div>
              </div>
            )}

            {viewMode === 'timeline' && (
              <div className="timeline-view">
                {(() => {
                  const teamStartYear = startYearMode === 'shared' 
                    ? sharedStartYear 
                    : (teamStartYears[selectedTeam.id] || sharedStartYear);
                  const timeline = getTimelineLayout(round.songs, teamStartYear);

                  const renderCard = (card: typeof timeline.orderedCards[number]) => {
                    const scores = teamScores[selectedTeam.id] || [];
                    const songScore = scores.find(s => s.songId === card.song.id);
                    const allCorrect = songScore && songScore.titleCorrect && songScore.artistCorrect && songScore.yearCorrect && songScore.positionCorrect;
                    const someCorrect = songScore && (songScore.titleCorrect || songScore.artistCorrect || songScore.yearCorrect || songScore.positionCorrect);
                    const points = songScore?.points || 0;
                    
                    return (
                      <div 
                        key={card.song.id} 
                        className={`timeline-card song-card ${allCorrect ? 'all-correct' : someCorrect ? 'some-correct' : 'none-correct'}`}
                      >
                        <div className="card-number-badge">Kaart {card.cardNumber}</div>
                        <div className={`card-position ${card.side}`}>{card.side === 'left' ? '◀ LINKS' : 'RECHTS ▶'}</div>
                        <div className="card-year">{card.song.year}</div>
                        <div className="card-content">
                          {songScore?.titleCorrect ? (
                            <div className="card-title correct">{card.song.title}</div>
                          ) : (
                            <div className="card-title hidden">???</div>
                          )}
                          {songScore?.artistCorrect ? (
                            <div className="card-artist correct">{card.song.artist}</div>
                          ) : (
                            <div className="card-artist hidden">???</div>
                          )}
                        </div>
                        <div className={`card-points ${points === 4 ? 'perfect' : points > 0 ? 'partial' : 'zero'}`}>
                          {points} pt
                        </div>
                      </div>
                    );
                  };

                  return (
                    <>
                      <div className="timeline-header">
                        <span className="th-left">◀ LINKS ({timeline.leftCards.length})</span>
                        <span className="th-center">Startjaar: {teamStartYear}</span>
                        <span className="th-right">RECHTS ({timeline.rightCards.length}) ▶</span>
                      </div>

                      <div className="timeline-order-line">
                        <div className="timeline-order-values">
                          {timeline.leftCards.map(card => (
                            <span key={`${card.song.id}-order-left`} className="timeline-order-chip">{card.cardNumber}</span>
                          ))}
                          <span className="timeline-order-chip start">START {teamStartYear}</span>
                          {timeline.rightCards.map(card => (
                            <span key={`${card.song.id}-order-right`} className="timeline-order-chip">{card.cardNumber}</span>
                          ))}
                        </div>
                      </div>
                      
                      <div className="timeline-strip-wrapper">
                        <div className="timeline-strip">
                          {timeline.leftCards.map(card => renderCard(card))}
                          
                          <div className="timeline-card start-card">
                            <div className="card-label">START</div>
                            <div className="card-year">{teamStartYear}</div>
                          </div>
                          
                          {timeline.rightCards.map(card => renderCard(card))}
                        </div>
                      </div>
                      
                      <div className="timeline-hint">
                        ← Scroll om alle kaartjes te zien →
                      </div>
                    </>
                  );
                })()}

                <div className="team-total">
                  <span>Totaal deze ronde:</span>
                  <span className="total-score">{getTeamTotalScore(selectedTeam.id)} punten</span>
                </div>
              </div>
            )}

            {viewMode === 'scan' && (
              <div className="scan-view">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  capture="environment"
                  style={{ display: 'none' }}
                  onChange={handleScanFileChange}
                />
                <div className="scan-intro">
                  <strong>📷 AI Kaartjes Scan</strong>
                  <span>Plak een JSON met alle kaartjes hieronder, of scan elk kaartje afzonderlijk met de camera.</span>
                </div>
                <div className="scan-json-section">
                  <div className="scan-json-header">
                    <span>📋 JSON plakken (alle kaartjes tegelijk)</span>
                    {Object.values(scanResults).some(r => r !== null) && (
                      <button className="scan-apply-all-btn" onClick={applyAllBatchResults}>
                        ✓ Alle scores toepassen
                      </button>
                    )}
                  </div>
                  <textarea
                    className="scan-json-input"
                    placeholder='Plak hier de JSON array, bijv. [{"nummer":1,"titel":"...","artiest":"...","jaar":"..."}]'
                    value={jsonInput}
                    onChange={e => setJsonInput(e.target.value)}
                    rows={5}
                  />
                  {jsonParseError && <div className="scan-error">{jsonParseError}</div>}
                  <button className="scan-json-btn" onClick={handleJsonBatch} disabled={!jsonInput.trim()}>
                    Nakijken
                  </button>
                </div>
                {sortRoundSongs(round.songs).map((song, idx) => {
                  const result = scanResults[song.id];
                  const loading = scanLoading[song.id];
                  const error = scanErrors[song.id];
                  const applied = scanApplied[song.id];
                  const ov = scanOverrides[song.id] || {};
                  const titleMatch = result?.title != null ? (ov.title !== undefined ? ov.title : isMatch(result.title, song.title)) : null;
                  const artistMatch = result?.artist != null ? (ov.artist !== undefined ? ov.artist : isMatch(result.artist, song.artist)) : null;
                  const yearMatch = result?.year != null ? (ov.year !== undefined ? ov.year : isMatch(result.year, String(song.year))) : null;
                  return (
                    <div key={song.id} className={`scan-card-row ${applied ? 'applied' : ''}`}>
                      <div className="scan-card-num">Kaart {idx + 1}</div>
                      <div className="scan-card-correct">
                        <span className="scan-correct-title">{song.title}</span>
                        <span className="scan-correct-artist">{song.artist}</span>
                        <span className="scan-correct-year">{song.year}</span>
                      </div>
                      <div className="scan-actions">
                        {loading ? (
                          <span className="scan-loading">⏳ Scannen...</span>
                        ) : applied ? (
                          <>
                            <span className="scan-applied-badge">✓ Toegepast</span>
                            <button className="scan-rescan-btn" onClick={() => startScan(song.id)}>🔄 Opnieuw</button>
                          </>
                        ) : (
                          <button className="scan-btn" onClick={() => startScan(song.id)}>📷 Scan</button>
                        )}
                      </div>
                      {error && <div className="scan-error">{error}</div>}
                      {result && !applied && (
                        <div className="scan-result">
                          <div className={`scan-field ${titleMatch ? 'match' : 'mismatch'}`}>
                            <span className="scan-field-label">Titel:</span>
                            <span className="scan-field-value">{result.title || '—'}</span>
                            <div className="scan-field-override">
                              <button className={`scan-override-ok${titleMatch ? ' active' : ''}`} onClick={() => setFieldOverride(song.id, 'title', true)} title="Goedkeuren">✓</button>
                              <button className={`scan-override-nok${!titleMatch ? ' active' : ''}`} onClick={() => setFieldOverride(song.id, 'title', false)} title="Afkeuren">✗</button>
                            </div>
                          </div>
                          <div className={`scan-field ${artistMatch ? 'match' : 'mismatch'}`}>
                            <span className="scan-field-label">Artiest:</span>
                            <span className="scan-field-value">{result.artist || '—'}</span>
                            <div className="scan-field-override">
                              <button className={`scan-override-ok${artistMatch ? ' active' : ''}`} onClick={() => setFieldOverride(song.id, 'artist', true)} title="Goedkeuren">✓</button>
                              <button className={`scan-override-nok${!artistMatch ? ' active' : ''}`} onClick={() => setFieldOverride(song.id, 'artist', false)} title="Afkeuren">✗</button>
                            </div>
                          </div>
                          <div className={`scan-field ${yearMatch ? 'match' : 'mismatch'}`}>
                            <span className="scan-field-label">Jaar:</span>
                            <span className="scan-field-value">{result.year || '—'}</span>
                            <div className="scan-field-override">
                              <button className={`scan-override-ok${yearMatch ? ' active' : ''}`} onClick={() => setFieldOverride(song.id, 'year', true)} title="Goedkeuren">✓</button>
                              <button className={`scan-override-nok${!yearMatch ? ' active' : ''}`} onClick={() => setFieldOverride(song.id, 'year', false)} title="Afkeuren">✗</button>
                            </div>
                          </div>
                          <button className="scan-apply-btn" onClick={() => applyScanResult(song.id, song)}>
                            Score toepassen
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {viewMode === 'teamApp' && event?.enableTeamApp && eventId && (
              <TeamAppOverview
                eventId={eventId}
                startYear={startYearMode === 'shared' ? sharedStartYear : undefined}
              />
            )}
          </div>
        )}

        {!selectedTeam && teams.length === 0 && (
          <div className="no-teams">
            <p>Geen teams in dit evenement. Voeg teams toe in het Events overzicht.</p>
            <Button onClick={() => navigate('/events')}>Naar Events</Button>
          </div>
        )}
      </div>

    {/* Scan review modal */}
    {reviewModal && selectedTeam && (
      <div className="scan-review-backdrop" onClick={() => setReviewModal(null)}>
        <div className="scan-review-modal" onClick={e => e.stopPropagation()}>
          <div className="scan-review-title">📷 Gescand kaartje controleren</div>
          <div className="scan-review-body">
            <div className="scan-review-col">
              <div className="scan-review-col-label">✓ Correct antwoord</div>
              <div className="scan-review-correct-row"><span>Titel</span><strong>{reviewModal.song.title}</strong></div>
              <div className="scan-review-correct-row"><span>Artiest</span><strong>{reviewModal.song.artist}</strong></div>
              <div className="scan-review-correct-row"><span>Jaar</span><strong>{reviewModal.song.year}</strong></div>
            </div>
            <div className="scan-review-col">
              <div className="scan-review-col-label">🤖 AI las</div>
              {(['title','artist','year'] as const).map(field => {
                const aiVal  = field === 'year' ? reviewModal.result.year : reviewModal.result[field as 'title'|'artist'];
                const correct = field === 'year' ? String(reviewModal.song.year) : reviewModal.song[field as 'title'|'artist'];
                const match  = reviewOverrides[field] !== undefined ? reviewOverrides[field] : (aiVal != null ? isMatch(aiVal, correct) : null);
                return (
                  <div key={field} className={`scan-review-ai-row ${match === true ? 'match' : match === false ? 'mismatch' : ''}`}>
                    <span>{aiVal || '—'}</span>
                    <div className="scan-review-btns">
                      <button className={`srb-ok${match === true ? ' active' : ''}`}  onClick={() => setReviewOverrides(p => ({...p, [field]: true}))}>✓</button>
                      <button className={`srb-nok${match === false ? ' active' : ''}`} onClick={() => setReviewOverrides(p => ({...p, [field]: false}))}>✗</button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          {reviewModal.result.positie != null && (
            <div className={`scan-review-positie ${reviewModal.result.positie ? 'match' : 'mismatch'}`}>
              Positie: {reviewModal.result.positie ? '✓ Correct' : '✗ Fout'}
            </div>
          )}
          <div className="scan-review-actions">
            <button className="scan-review-cancel" onClick={() => setReviewModal(null)}>Sluiten</button>
            <button className="scan-review-confirm" onClick={applyReviewModal}>✓ Score toepassen</button>
          </div>
        </div>
      </div>
    )}

    {/* Scan submissions modal — participant-submitted scans per song */}
    {scanSubmissionsModal && (
      <div className="scan-sub-backdrop" onClick={() => setScanSubmissionsModal(null)}>
        <div className="scan-sub-modal" onClick={e => e.stopPropagation()}>
          <div className="scan-sub-header">
            <span className="scan-sub-title">📷 Scan inzendingen</span>
            <span className="scan-sub-song">{scanSubmissionsModal.songTitle} · {scanSubmissionsModal.songArtist} · {scanSubmissionsModal.songYear}</span>
          </div>
          <div className="scan-sub-correct-bar">
            <span>Correct antwoord:</span>
            <strong>{scanSubmissionsModal.songTitle}</strong>
            <span>·</span>
            <strong>{scanSubmissionsModal.songArtist}</strong>
            <span>·</span>
            <strong>{scanSubmissionsModal.songYear}</strong>
          </div>
          {scanSubmissionsModal.entries.length === 0 ? (
            <div className="scan-sub-empty">Geen scan inzendingen voor dit nummer.</div>
          ) : (
            <div className="scan-sub-list">
              {scanSubmissionsModal.entries.map(({ team, submission }) => {
                const titleOk = isMatch(submission.title || '', scanSubmissionsModal.songTitle);
                const artistOk = isMatch(submission.artist || '', scanSubmissionsModal.songArtist);
                const yearOk = isMatch(submission.year || '', String(scanSubmissionsModal.songYear));
                return (
                  <div key={team.id} className={`scan-sub-entry status-${submission.reviewStatus}`}>
                    <div className="scan-sub-team-name">{team.name}</div>
                    <div className="scan-sub-fields">
                      <div className={`scan-sub-field ${titleOk ? 'ok' : 'nok'}`}>
                        <span className="scan-sub-label">Titel</span>
                        <span className="scan-sub-value">{submission.title || '—'}</span>
                        <span className="scan-sub-match">{titleOk ? '✓' : '✗'}</span>
                      </div>
                      <div className={`scan-sub-field ${artistOk ? 'ok' : 'nok'}`}>
                        <span className="scan-sub-label">Artiest</span>
                        <span className="scan-sub-value">{submission.artist || '—'}</span>
                        <span className="scan-sub-match">{artistOk ? '✓' : '✗'}</span>
                      </div>
                      <div className={`scan-sub-field ${yearOk ? 'ok' : 'nok'}`}>
                        <span className="scan-sub-label">Jaar</span>
                        <span className="scan-sub-value">{submission.year || '—'}</span>
                        <span className="scan-sub-match">{yearOk ? '✓' : '✗'}</span>
                      </div>
                    </div>
                    <div className="scan-sub-review-row">
                      {submission.reviewStatus !== 'pending' && (
                        <span className={`scan-sub-badge badge-${submission.reviewStatus}`}>
                          {submission.reviewStatus === 'approved' ? '✓ Goedgekeurd' : '✗ Afgekeurd'}
                        </span>
                      )}
                      <button
                        className={`scan-sub-approve${submission.reviewStatus === 'approved' ? ' active' : ''}`}
                        onClick={() => reviewScanSubmission(team.id, submission.songId, 'approved')}
                      >✓ Goed</button>
                      <button
                        className={`scan-sub-reject${submission.reviewStatus === 'rejected' ? ' active' : ''}`}
                        onClick={() => reviewScanSubmission(team.id, submission.songId, 'rejected')}
                      >✗ Afkeuren</button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          <div className="scan-sub-footer">
            <button className="scan-sub-refresh" onClick={() => { loadEvent(); setScanSubmissionsModal(null); }} title="Ververs scores vanuit deelnemers">🔄 Ververs</button>
            <button className="scan-sub-close" onClick={() => setScanSubmissionsModal(null)}>Sluiten</button>
          </div>
        </div>
      </div>
    )}
    </div>
  );
};

export default RoundScoring;
